import { Component, type ErrorInfo, type ReactNode } from 'react'

import { ApiError } from '@/lib/api'

interface State {
  error: Error | null
}

/**
 * Top-level error boundary.
 *
 * CLAUDE.md Part II §13 — a refusal carries a `decision_ref` and the operator
 * must be able to copy it. Even when a screen crashes rather than handling the
 * refusal, the reference survives to this boundary.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No PII, no payloads — invariant 13 and the PCI posture both apply to logs.
    console.error('console error boundary', {
      name: error.name,
      message: error.message,
      componentStack: info.componentStack,
    })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children

    const decisionRef = error instanceof ApiError ? error.decisionRef : null
    const requestId = error instanceof ApiError ? error.requestId : null

    return (
      <main className="error-boundary" role="alert">
        <h1>Something went wrong</h1>
        <p>{error.message}</p>
        {decisionRef !== null && (
          <p>
            Decision reference: <code>{decisionRef}</code>
          </p>
        )}
        {requestId !== null && (
          <p>
            Request id: <code>{requestId}</code>
          </p>
        )}
        <button type="button" onClick={() => this.setState({ error: null })}>
          Try again
        </button>
      </main>
    )
  }
}
